import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, OutlinedInput, Tooltip, Typography, Card, CardContent, CardActionArea, IconButton } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import DataTable from "../../../../common/DataTable";
import { customFieldColDef } from "../SoftwareAdmin/ColumnDefs";
import { LoadingPage } from "../../../../common/LoadingPage";
import { apiRequest } from "../../../../common/common";
import { showNotification } from "../../../../common/Notification/ToastifyNotification";
import { useSelector } from "react-redux";
import { Delete, Edit, ArrowBack, Description, LocalShipping, AttachMoney, Group } from "@mui/icons-material";

const fieldsArr = [
    {
        name: 'Payment Terms',
        value: 'payment',
        description: 'Manage payment terms for your organization',
        icon: <AttachMoney color="primary" sx={{ fontSize: 40 }} />,
        color: '#4CAF50'
    },
    {
        name: 'Freight Terms',
        value: 'shipping',
        description: 'Manage shipping and freight terms',
        icon: <LocalShipping color="primary" sx={{ fontSize: 40 }} />,
        color: '#2196F3'
    },
    {
        name: 'Bid Group',
        value: 'bidgroup',
        description: 'Manage bid groups for procurement',
        icon: <Group color="primary" sx={{ fontSize: 40 }} />,
        color: '#FF9800'
    },
];

const CustomFields = () => {
    const { user } = useSelector(state => state.users);
    const [rowsPerPage, setRowPerPage] = useState(10);
    const [rowData, setRowData] = useState([]);
    const [selectedField, setSelectedField] = useState(null);
    const [dataLoading, setDataLoading] = useState(false);
    const [name, setName] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [actionId, setActionId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [viewMode, setViewMode] = useState('selection'); // 'selection' or 'details'

    const onPaginationChanged = (event) => {
        if (event.api) {
            const newPageSize = event.api.paginationGetPageSize();
            setRowPerPage(newPageSize);
        }
    };

    const fetchPaymentTerms = async () => {
        setDataLoading(true);
        try {
            const response = await apiRequest('get', `/org-teams?organizationId=${user?.organization?.organization_id}&type=payment`);
            const { data } = response?.data;
            setRowData(data);
            setDataLoading(false);
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    }

    const fetchShippingTerms = async () => {
        setDataLoading(true);
        try {
            const response = await apiRequest('get', `/org-teams?organizationId=${user?.organization?.organization_id}&type=shipping`);
            const { data } = response?.data;
            setRowData(data);
            setDataLoading(false);
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    };

    const fetchBidGroup = async () => {
        setDataLoading(true);
        try {
            const response = await apiRequest('get', `/bid-group?organizationId=${user?.organization?.organization_id}`);
            const { data } = response?.data;
            setRowData(data);
            setDataLoading(false);
        } catch (error) {
            setDataLoading(false);
            showNotification(error?.response?.data.error || error.message, 'error');
        }
    };

    useEffect(() => {
        if (selectedField && viewMode === 'details') {
            if (selectedField === 'payment') {
                fetchPaymentTerms();
            } else if (selectedField === 'shipping') {
                fetchShippingTerms();
            } else if (selectedField === 'bidgroup') {
                fetchBidGroup();
            }
        } else {
            setRowData([]);
        }
    }, [selectedField, viewMode]);

    const handleFieldSelect = (field) => {
        setSelectedField(field.value);
        setViewMode('details');
    };

    const handleBackToSelection = () => {
        setSelectedField(null);
        setViewMode('selection');
        setRowData([]);
    };

    const getFieldTitle = () => {
        const field = fieldsArr.find(f => f.value === selectedField);
        return field ? field.name : 'Custom Field';
    };

    const getFieldIcon = () => {
        const field = fieldsArr.find(f => f.value === selectedField);
        return field ? field.icon : <Description />;
    };

    const updatedColumns = useMemo(() => {
        let column = customFieldColDef;
        if (column.length > 0) {
            return column.map((col) => {
                if (col.field === 'action') {
                    return {
                        ...col,
                        minWidth: 130,
                        width: 130,
                        cellRenderer: (params) => (
                            <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Tooltip title="Edit">
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={async () => {
                                            setDialogOpen(true);
                                            setActionId(params?.data?.teams_id || params?.data?.bid_group_id);
                                            setName(params?.data?.teams || params?.data?.name);
                                        }}
                                    >
                                        <Edit fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => {
                                            setDeleteOpen(true);
                                            setActionId(params?.data?.teams_id || params?.data?.bid_group_id);
                                        }}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )
                    };
                }
                if (col.field === 'name') {
                    return {
                        ...col,
                        minWidth: 200,
                        cellRenderer: (params) => {
                            return <Typography variant="body2">{params?.data?.name || params?.data?.teams}</Typography>
                        },
                    };
                }
                return col;
            })
        }
        return column;
    }, [customFieldColDef]);

    // Field Selection View
    const renderSelectionView = () => (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant='h4' style={{ fontWeight: 'bold' }}>Custom Fields</Typography>
            </Box>
            <Typography variant="subtitle1" color="text.secondary" mb={3}>
                Select a custom field type to manage
            </Typography>
            <Grid container spacing={3}>
                {fieldsArr.map((field) => (
                    <Grid item xs={12} md={4} key={field.value}>
                        <Card
                            sx={{
                                height: '100%',
                                border: '1px solid #e0e0e0',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 6,
                                    borderColor: field.color,
                                }
                            }}
                        >
                            <CardActionArea
                                sx={{ height: '100%', p: 2 }}
                                onClick={() => handleFieldSelect(field)}
                            >
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Box sx={{ mb: 2 }}>
                                        {field.icon}
                                    </Box>
                                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: field.color }}>
                                        {field.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {field.description}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        sx={{ mt: 2 }}
                                        size="small"
                                    >
                                        Manage
                                    </Button>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    // Field Details View
    const renderDetailsView = () => {
        const selectedFieldObj = fieldsArr.find(f => f.value === selectedField);

        return (
            <Box>
                <Box display="flex" alignItems="center" mb={3}>
                    <IconButton
                        onClick={handleBackToSelection}
                        sx={{ mr: 2 }}
                        color="primary"
                    >
                        <ArrowBack />
                    </IconButton>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Box sx={{ color: selectedFieldObj.color }}>
                            {selectedFieldObj.icon}
                        </Box>
                        <Box>
                            <Typography variant='h4' style={{ fontWeight: 'bold' }}>
                                {selectedFieldObj.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedFieldObj.description}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        size="medium"
                        color="primary"
                        variant="contained"
                        onClick={() => setDialogOpen(true)}
                        startIcon={<Edit />}
                    >
                        Add New
                    </Button>
                </Box>

                {dataLoading ? (
                    <LoadingPage isLoading={dataLoading} />
                ) : rowData.length === 0 ? (
                    <Box
                        sx={{
                            textAlign: 'center',
                            py: 8,
                            border: '1px dashed #e0e0e0',
                            borderRadius: 2
                        }}
                    >
                        <Description sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No {selectedFieldObj.name.toLowerCase()} found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            Get started by adding your first {selectedFieldObj.name.toLowerCase()}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setDialogOpen(true)}
                        >
                            Add New {selectedFieldObj.name}
                        </Button>
                    </Box>
                ) : (
                    <DataTable
                        height="70vh"
                        columnDefs={updatedColumns}
                        rowData={rowData}
                        rowsPerPage={rowsPerPage}
                        paginationPageSizeSelector={[10, 15, 25, 50, 100]}
                        onPaginationChanged={onPaginationChanged}
                        modelType="clientSide"
                    />
                )}
            </Box>
        );
    };

    return (
        <>
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteOpen} fullWidth maxWidth="sm">
                <DialogTitle>
                    {`Delete ${getFieldTitle()}`}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to permanently delete this item? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={async () => {
                            setDataLoading(true);
                            try {
                                let response;
                                if (selectedField === 'payment' || selectedField === 'shipping') {
                                    response = await apiRequest('delete', `/org-teams/${actionId}`);
                                } else {
                                    response = await apiRequest('delete', `/bid-group/${actionId}`)
                                }
                                if (response.data) {
                                    setDeleteOpen(false);
                                    setActionId(null);
                                    showNotification(`${getFieldTitle()} deleted successfully`, 'success');
                                    if (selectedField === 'payment') {
                                        await fetchPaymentTerms();
                                    } else if (selectedField === 'shipping') {
                                        await fetchShippingTerms();
                                    } else if (selectedField === 'bidgroup') {
                                        await fetchBidGroup();
                                    }
                                }
                            } catch (error) {
                                showNotification(error?.response?.data.error || error.message, 'error')
                            } finally {
                                setDataLoading(false);
                            }
                        }}
                    >
                        Confirm Delete
                    </Button>
                    <Button
                        color="inherit"
                        variant="outlined"
                        onClick={() => {
                            setDeleteOpen(false);
                            setActionId(null);
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} fullWidth maxWidth="sm">
                <DialogTitle>
                    {actionId ? `Update ${getFieldTitle()}` : `Add New ${getFieldTitle()}`}
                </DialogTitle>
                <DialogContent>
                    {dataLoading && (<LoadingPage isLoading={dataLoading} />)}
                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>{getFieldTitle()}</InputLabel>
                                <OutlinedInput
                                    label={getFieldTitle()}
                                    value={name || ''}
                                    onChange={(e) => setName(e.target.value)}
                                    autoFocus
                                    sx={{
                                        height: '45px',
                                        padding: '0px',
                                        '& .MuiOutlinedInput-input': {
                                            height: '100%',
                                            padding: '0 14px',
                                            boxSizing: 'border-box',
                                        }
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={async () => {
                            if (!name?.trim()) {
                                showNotification('Please enter a valid name', 'error');
                                return;
                            }

                            setDataLoading(true);
                            try {
                                let response;
                                const data = {
                                    organization_id: user?.organization?.organization_id
                                };

                                if (selectedField === 'payment') {
                                    data.teams = name;
                                    data.type = 'payment';
                                } else if (selectedField === 'shipping') {
                                    data.type = 'shipping';
                                    data.teams = name;
                                } else {
                                    data.name = name;
                                }

                                if (selectedField === 'payment' || selectedField === 'shipping') {
                                    if (actionId !== null) {
                                        response = await apiRequest('put', `/org-teams/${actionId}`, data);
                                    } else {
                                        response = await apiRequest('post', '/org-teams', data);
                                    }
                                } else {
                                    if (actionId !== null) {
                                        response = await apiRequest('put', `/bid-group/${actionId}`, data);
                                    } else {
                                        response = await apiRequest('post', '/bid-group', data);
                                    }
                                }

                                if (response.data) {
                                    showNotification(
                                        actionId
                                            ? `${getFieldTitle()} updated successfully`
                                            : `${getFieldTitle()} created successfully`,
                                        'success'
                                    );
                                    setDialogOpen(false);
                                    setName(null);
                                    setActionId(null);

                                    // Refresh data
                                    if (selectedField === 'payment') {
                                        await fetchPaymentTerms();
                                    } else if (selectedField === 'shipping') {
                                        await fetchShippingTerms();
                                    } else if (selectedField === 'bidgroup') {
                                        await fetchBidGroup();
                                    }
                                }
                            } catch (error) {
                                showNotification(error?.response?.data.error || error.message, 'error');
                            } finally {
                                setDataLoading(false);
                            }
                        }}
                    >
                        {actionId ? 'Update' : 'Create'}
                    </Button>
                    <Button
                        color="secondary"
                        variant="outlined"
                        onClick={() => {
                            setName(null);
                            setDialogOpen(false);
                            setActionId(null);
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Main Content */}
            <Box sx={{ p: 2 }}>
                {viewMode === 'selection' ? renderSelectionView() : renderDetailsView()}
            </Box>
        </>
    );
};

export default CustomFields;